import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatINR } from "@/lib/roles";
import { Loader2, Calculator } from "lucide-react";

export default function PropertyRegister() {
  const nav = useNavigate();
  const [wards, setWards] = useState([]);
  const [bodies, setBodies] = useState([]);
  const [form, setForm] = useState({
    owner_name: "", owner_email: "", owner_phone: "", owner_aadhaar: "",
    property_type: "residential", usage_type: "self_occupied",
    ward: "", zone: "North",
    address: "", plot_number: "",
    built_up_area: 1000, plot_area: 1200, number_of_floors: 1, year_of_construction: 2020,
    latitude: 30.9010, longitude: 75.8573,
    municipal_body: "",
  });
  const [tax, setTax] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/master/wards"), api.get("/master/municipal-bodies")]).then(([a, b]) => {
      setWards(a.data.items || []);
      setBodies(b.data.items || []);
      setForm((f) => ({ ...f, ward: a.data.items?.[0] || "", municipal_body: b.data.items?.[0] || "" }));
    });
  }, []);

  const calcTax = async () => {
    const { data } = await api.post("/properties/calculate-tax", {
      built_up_area: Number(form.built_up_area),
      property_type: form.property_type,
      usage_type: form.usage_type,
    });
    setTax(data);
  };
  useEffect(() => { if (form.built_up_area) calcTax(); /* eslint-disable-next-line */ }, [form.built_up_area, form.property_type, form.usage_type]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form,
        owner_aadhaar: form.owner_aadhaar ? `XXXX-XXXX-${form.owner_aadhaar}` : "",
        built_up_area: Number(form.built_up_area),
        plot_area: Number(form.plot_area),
        number_of_floors: Number(form.number_of_floors),
        year_of_construction: Number(form.year_of_construction),
        latitude: Number(form.latitude), longitude: Number(form.longitude),
      };
      const { data } = await api.post("/properties", payload);
      toast.success(`Property ${data.property_id} registered`);
      nav(`/properties/${data.id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="property-register-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Property Tax</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900 dark:text-white">Register new property</h1>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base font-heading">Owner details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Owner name*</Label><Input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="reg-owner-name" /></div>
              <div><Label>ID (last 4 digits)</Label><Input maxLength={4} inputMode="numeric" value={form.owner_aadhaar} onChange={(e) => setForm({ ...form, owner_aadhaar: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="1234" data-testid="input-owner-id-last4" /></div>
              <div><Label>Email</Label><Input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} /></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base font-heading">Property details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Property type</Label>
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Usage</Label>
                <Select value={form.usage_type} onValueChange={(v) => setForm({ ...form, usage_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self_occupied">Self-occupied</SelectItem>
                    <SelectItem value="rented">Rented</SelectItem>
                    <SelectItem value="vacant">Vacant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Municipal body</Label>
                <Select value={form.municipal_body} onValueChange={(v) => setForm({ ...form, municipal_body: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{bodies.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Ward</Label>
                <Select value={form.ward} onValueChange={(v) => setForm({ ...form, ward: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{wards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Zone</Label>
                <Select value={form.zone} onValueChange={(v) => setForm({ ...form, zone: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["North", "South", "East", "West", "Central"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Plot number</Label><Input value={form.plot_number} onChange={(e) => setForm({ ...form, plot_number: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Address*</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="reg-address" /></div>
              <div><Label>Built-up area (sq.ft)*</Label><Input required type="number" value={form.built_up_area} onChange={(e) => setForm({ ...form, built_up_area: e.target.value })} data-testid="reg-area" /></div>
              <div><Label>Plot area (sq.ft)</Label><Input type="number" value={form.plot_area} onChange={(e) => setForm({ ...form, plot_area: e.target.value })} /></div>
              <div><Label>Floors</Label><Input type="number" value={form.number_of_floors} onChange={(e) => setForm({ ...form, number_of_floors: e.target.value })} /></div>
              <div><Label>Year built</Label><Input type="number" value={form.year_of_construction} onChange={(e) => setForm({ ...form, year_of_construction: e.target.value })} /></div>
              <div><Label>Latitude</Label><Input type="number" step="0.00001" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></div>
              <div><Label>Longitude</Label><Input type="number" step="0.00001" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="sticky top-24" data-testid="tax-calculator-card">
            <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Calculator className="w-4 h-4" /> Tax calculator</CardTitle></CardHeader>
            <CardContent>
              <div className="text-xs uppercase tracking-wider text-slate-500">Estimated annual tax</div>
              <div className="text-3xl font-heading font-bold tabular-nums text-[#0F2942] dark:text-white mt-1" data-testid="calc-tax-amount">
                {tax ? formatINR(tax.annual_tax) : "—"}
              </div>
              {tax && (
                <div className="mt-4 space-y-1 text-xs text-slate-500">
                  <div>Base rate: ₹{tax.breakdown.base_rate_per_sqft}/sq.ft</div>
                  <div>Usage multiplier: ×{tax.breakdown.usage_multiplier}</div>
                  <div>Area: {tax.breakdown.built_up_area} sq.ft</div>
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full mt-6 bg-[#0F2942]" data-testid="btn-submit-property">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Register property"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
